import { CAR_BY_ID } from './cars.js';
import {
  isWeather,
  type PublicRoom,
  type RaceSettings,
} from './types.js';

const RACE_PHASES = new Set(['lobby', 'countdown', 'racing', 'finished']);
const NPC_DIFFICULTIES = new Set(['rookie', 'sport', 'pro']);
const TIMES_OF_DAY = new Set(['dawn', 'day', 'sunset', 'night']);
const EVENT_MODES = new Set([
  'circuit',
  'sprint',
  'time-trial',
  'style-rush',
  'elimination',
  'heat-run',
  'road-rage',
]);
const ARCADE_PRESETS = new Set([
  'standard',
  'nitro-rush',
  'heavy-metal',
  'traffic-panic',
  'low-gravity',
]);
const WAGERS = new Set(['clean-finish', 'clean-passes', 'extreme-route']);

export function parsePublicRoom(value: unknown): PublicRoom | null {
  if (!isRecord(value) || !validIdentifier(value.id) || !validIdentifier(value.hostPlayerId)) return null;
  const settings = parseRaceSettings(value.settings);
  if (
    !settings
    || !RACE_PHASES.has(String(value.phase))
    || !integer(value.playerCount, 0, 6)
    || !finite(value.createdAt)
    || !finite(value.expiresAt)
    || value.expiresAt < value.createdAt
    || (value.sessionTtlMs !== undefined && !integer(value.sessionTtlMs, 60_000, 30 * 24 * 60 * 60_000))
    || (value.raceAttempt !== undefined && !integer(value.raceAttempt, 1, Number.MAX_SAFE_INTEGER))
    || (value.spectatorCount !== undefined && !integer(value.spectatorCount, 0, 12))
    || !validRoomMembers(value.members)
    || !validRoomMemberConsistency(value)
  ) return null;
  return value as unknown as PublicRoom;
}

export function isValidPublicRoom(value: unknown): value is PublicRoom {
  return parsePublicRoom(value) !== null;
}

function parseRaceSettings(value: unknown): RaceSettings | null {
  if (
    !isRecord(value)
    || !validIdentifier(value.mapId)
    || !integer(value.npcCount, 0, 11)
    || !NPC_DIFFICULTIES.has(String(value.npcDifficulty))
    || !integer(value.maxPlayers, 1, 6)
    || !optionalBoolean(value.antiCheat)
    || !optionalBoolean(value.collisions)
    || !optionalBoolean(value.traffic)
    || !optionalBoolean(value.timeTrial)
    || (value.timeOfDay !== undefined && !TIMES_OF_DAY.has(String(value.timeOfDay)))
    || (value.weather !== undefined && !isWeather(value.weather))
    || (value.eventMode !== undefined && !EVENT_MODES.has(String(value.eventMode)))
    || (value.arcadePreset !== undefined && !ARCADE_PRESETS.has(String(value.arcadePreset)))
    || (value.tourId !== undefined && !validIdentifier(value.tourId))
    || (value.tourStage !== undefined && !integer(value.tourStage, 0, 31))
    || (value.featuredRival !== undefined && !validText(value.featuredRival, 24))
    || (value.wager !== undefined && !WAGERS.has(String(value.wager)))
    || (value.dailyChallengeId !== undefined
      && (typeof value.dailyChallengeId !== 'string' || !validUtcDateId(value.dailyChallengeId)))
    || (value.rewindLimit !== undefined && !integer(value.rewindLimit, 0, 8))
    || (value.npcRoster !== undefined && (
      !Array.isArray(value.npcRoster)
      || value.npcRoster.length > 11
      || !value.npcRoster.every((entry) => NPC_DIFFICULTIES.has(String(entry)))
    ))
    || (value.visibility !== undefined && value.visibility !== 'private' && value.visibility !== 'public')
    || (value.laps !== undefined && !integer(value.laps, 1, 10))
  ) return null;
  return value as unknown as RaceSettings;
}

function validRoomMembers(value: unknown): boolean {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 6) return false;
  const ids = new Set<string>();
  return value.every((member) => {
    if (
      !isRecord(member)
      || !validIdentifier(member.id)
      || ids.has(member.id)
      || !validText(member.name, 24)
      || !validIdentifier(member.carId)
      || !CAR_BY_ID.has(member.carId)
      || typeof member.connected !== 'boolean'
      || typeof member.ready !== 'boolean'
      || typeof member.host !== 'boolean'
    ) return false;
    ids.add(member.id);
    return true;
  });
}

function validRoomMemberConsistency(room: Record<string, unknown>): boolean {
  if (room.members === undefined) return true;
  if (!Array.isArray(room.members) || !Number.isInteger(room.playerCount)) return false;
  if (room.members.length !== room.playerCount) return false;
  const hostMembers = room.members.filter((member) => (
    isRecord(member) && member.host === true
  ));
  return hostMembers.length === 1
    && isRecord(hostMembers[0])
    && hostMembers[0].id === room.hostPlayerId;
}

function validIdentifier(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function validText(value: unknown, maximumLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maximumLength;
}

function validUtcDateId(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function optionalBoolean(value: unknown): boolean {
  return value === undefined || typeof value === 'boolean';
}

function integer(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === 'number'
    && Number.isInteger(value)
    && value >= minimum
    && value <= maximum;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
